import { TrendingDown, X, Plus, ChevronDown, TrendingUp } from 'lucide-react';
import { PriceTracking, PriceHistory } from '../types';
import { useState } from 'react';

interface PriceTrackerProps {
  trackingList: PriceTracking[];
  priceHistory: Record<string, PriceHistory[]>;
  onRemoveTracking: (trackingId: string) => void;
  onAddNewTracking?: () => void;
}

export function PriceTracker({ trackingList, priceHistory, onRemoveTracking, onAddNewTracking }: PriceTrackerProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  if (trackingList.length === 0) {
    return (
      <div className="card p-8 sm:p-12 md:p-16 text-center animate-fade-in">
        <div className="max-w-md mx-auto">
          <div className="w-14 h-14 sm:w-16 sm:h-16 mx-auto mb-4 sm:mb-6 rounded-2xl bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center">
            <TrendingDown size={28} className="sm:w-8 sm:h-8 text-neutral-400 dark:text-neutral-600" />
          </div>
          <h3 className="text-lg sm:text-xl font-bold text-neutral-900 dark:text-neutral-100 mb-2">No Tracked Prices</h3>
          <p className="text-sm sm:text-base text-neutral-500 dark:text-neutral-400 mb-4 sm:mb-6">Start tracking prices to monitor deals and get alerts when prices drop.</p>
          {onAddNewTracking && (
            <button 
              onClick={onAddNewTracking} 
              className="btn-primary px-5 sm:px-6 py-2.5 sm:py-3 rounded-xl text-sm sm:text-base"
              aria-label="Start tracking prices"
            >
              <Plus size={16} className="sm:w-[18px] sm:h-[18px]" aria-hidden="true" />
              Start Tracking
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2 sm:space-y-3 animate-fade-in">
      {trackingList.map((tracking) => {
        const history = priceHistory[tracking.id] || [];
        const isExpanded = expandedId === tracking.id;
        const currentPrice = tracking.current_price || 0;
        const lowestPrice = tracking.lowest_price || currentPrice;
        const highestPrice = tracking.highest_price || currentPrice;
        const priceChangePercent = lowestPrice ? ((currentPrice - lowestPrice) / lowestPrice) * 100 : 0;
        const isAboveLowest = currentPrice > lowestPrice;
        const isPriceDrop = currentPrice < highestPrice;

        return (
          <div key={tracking.id} className="card overflow-hidden">
            <button
              onClick={() => setExpandedId(isExpanded ? null : tracking.id)}
              className="w-full p-4 sm:p-5 flex items-start justify-between hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors duration-200 group"
            >
              <div className="flex-1 text-left space-y-2 sm:space-y-3 min-w-0">
                <h3 className="font-semibold text-neutral-900 dark:text-neutral-100 line-clamp-2 group-hover:text-accent-600 dark:group-hover:text-accent-400 transition-colors text-sm sm:text-base">
                  {tracking.title}
                </h3>
                <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-xs sm:text-sm">
                  <div>
                    <span className="text-neutral-500 dark:text-neutral-400 text-[10px] sm:text-xs uppercase tracking-wider">Current</span>
                    <div className="text-lg sm:text-xl font-bold text-neutral-900 dark:text-neutral-100 mt-0.5">${currentPrice.toLocaleString()}</div>
                  </div>
                  <div>
                    <span className="text-neutral-500 dark:text-neutral-400 text-[10px] sm:text-xs uppercase tracking-wider">Lowest</span>
                    <div className="text-base sm:text-lg font-semibold text-green-600 dark:text-green-400 mt-0.5">${lowestPrice.toLocaleString()}</div>
                  </div>
                  {tracking.target_price && (
                    <div>
                      <span className="text-neutral-500 dark:text-neutral-400 text-[10px] sm:text-xs uppercase tracking-wider">Target</span>
                      <div className="text-base sm:text-lg font-semibold text-accent-600 dark:text-accent-400 mt-0.5">
                        ${tracking.target_price.toLocaleString()}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 sm:gap-3 ml-3 sm:ml-4 flex-shrink-0">
                {isPriceDrop && priceChangePercent < 0 && (
                  <div className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-[10px] sm:text-xs font-semibold">
                    <TrendingDown size={12} className="sm:w-3.5 sm:h-3.5" />
                    {Math.abs(priceChangePercent).toFixed(1)}%
                  </div>
                )}
                {isAboveLowest && priceChangePercent > 0 && (
                  <div className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 text-[10px] sm:text-xs font-semibold">
                    <TrendingUp size={12} className="sm:w-3.5 sm:h-3.5" />
                    +{priceChangePercent.toFixed(1)}%
                  </div>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveTracking(tracking.id);
                  }}
                  className="p-1.5 sm:p-2 hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500 dark:text-red-400 rounded-lg transition-colors duration-200"
                  aria-label="Remove tracking"
                >
                  <X size={16} className="sm:w-[18px] sm:h-[18px]" />
                </button>
                <ChevronDown
                  size={16}
                  className={`text-neutral-400 dark:text-neutral-500 transition-transform duration-200 sm:w-[18px] sm:h-[18px] ${isExpanded ? 'rotate-180' : ''}`}
                />
              </div>
            </button>

            {isExpanded && history.length > 0 && (
              <div className="border-t border-neutral-200 dark:border-neutral-700 p-4 sm:p-5 bg-neutral-50 dark:bg-neutral-800/50 space-y-3 sm:space-y-4 animate-slide-up">
                <div className="space-y-2 sm:space-y-3">
                  <h4 className="font-semibold text-neutral-900 dark:text-neutral-100 text-xs sm:text-sm uppercase tracking-wider">Price History</h4>
                  <div className="space-y-2 max-h-48 sm:max-h-64 overflow-y-auto">
                    {history.map((entry, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between text-xs sm:text-sm bg-white dark:bg-neutral-800 p-2.5 sm:p-3 rounded-lg border border-neutral-200 dark:border-neutral-700"
                      >
                        <span className="text-neutral-600 dark:text-neutral-400 font-medium">
                          {new Date(entry.recorded_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })}
                        </span>
                        <span className="font-bold text-neutral-900 dark:text-neutral-100">${entry.price.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {history.length > 1 && (
                  <div className="card p-3 sm:p-4 space-y-2 sm:space-y-3">
                    <div className="text-[10px] sm:text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Statistics</div>
                    <div className="grid grid-cols-2 gap-3 sm:gap-4 text-xs sm:text-sm">
                      <div>
                        <span className="text-neutral-600 dark:text-neutral-400">Highest: </span>
                        <span className="font-bold text-neutral-900 dark:text-neutral-100">
                          ${Math.max(...history.map((h) => h.price)).toLocaleString()}
                        </span>
                      </div>
                      <div>
                        <span className="text-neutral-600 dark:text-neutral-400">Average: </span>
                        <span className="font-bold text-neutral-900 dark:text-neutral-100">
                          $
                          {(
                            history.reduce((sum, h) => sum + h.price, 0) / history.length
                          ).toLocaleString(undefined, {
                            maximumFractionDigits: 0,
                          })}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
